import {
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
  useQuery,
} from "@apollo/client";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Simple error deduplication for non-network errors
class SimpleErrorHandler {
  private recentErrors = new Set<string>();
  private readonly ERROR_COOLDOWN = 3000; // 3 seconds

  handleError(errorMessage: string): boolean {
    // Simple deduplication for regular errors
    if (!this.recentErrors.has(errorMessage)) {
      this.recentErrors.add(errorMessage);

      toast.error(<p className="toast">{errorMessage}</p>);

      // Clear the error from recent list after cooldown
      setTimeout(() => {
        this.recentErrors.delete(errorMessage);
      }, this.ERROR_COOLDOWN);

      return true;
    }
    return false;
  }
}

// Global instance to maintain state across hook instances
const errorHandler = new SimpleErrorHandler();

interface UseCustomQueryProps<
  T,
  TVariables extends OperationVariables = OperationVariables,
> extends Omit<QueryHookOptions<T, TVariables>, "onError"> {
  query: TypedDocumentNode<T, TVariables>;
  showErrorToast?: boolean;
}

const useCustomQuery = <
  T,
  TVariables extends OperationVariables = OperationVariables,
>({
  query,
  showErrorToast = false,
  ...options
}: UseCustomQueryProps<T, TVariables>) => {
  const navigate = useNavigate();

  const {
    data,
    loading,
    refetch,
    error,
    fetchMore,
    startPolling,
    stopPolling,
  } = useQuery<T, TVariables>(query, {
    fetchPolicy: "cache-and-network",
    ...options,
    onError(error) {
      if (error?.message !== "unauthenticated") {
        if (showErrorToast) {
          const errorMessage = error?.message ?? "An error occurred";
          // Only handle non-network errors here (network errors handled in Apollo errorLink)
          errorHandler.handleError(errorMessage);
        }
      } else {
        navigate("/clear");
      }
    },
  });

  return {
    data,
    isLoading: loading && !data,
    isRefetching: data && loading,
    refetch,
    startPolling,
    stopPolling,
    error: error,
    fetchMore: fetchMore,
  };
};

export default useCustomQuery;
